import React from 'react';
import { Upload, FilePlus, AlertCircle } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../store';
import NERViewer from './NERViewer';
import type { AnalysisResults } from '../types';
import { config } from '../utils/config';

const MainContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const { documents, currentDocument, loading, error } = useAppSelector(state => state.documents);
  const [file, setFile] = React.useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = React.useState<number | null>(null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    if (selectedFile.type !== 'application/pdf') {
        dispatch({ type: 'documents/setError', payload: 'Please upload a PDF file' });
        setFile(null);  // Clear the file state
        return;
    }

    setFile(selectedFile);
    await analyzeFile(selectedFile);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    // Check if the dragged file is a PDF
    const items = event.dataTransfer.items;
    if (items?.length > 0) {
        const item = items[0];
        if (item.type !== 'application/pdf') {
            // Add visual indication that file type is not allowed
            event.dataTransfer.dropEffect = 'none';
        } else {
            // Show that dropping is allowed
            event.dataTransfer.dropEffect = 'copy';
        }
    }
  };

  const handleDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    
    const droppedFile = event.dataTransfer.files[0];
    if (!droppedFile) return;

    if (droppedFile.type !== 'application/pdf') {
        dispatch({ type: 'documents/setError', payload: 'Please upload a PDF file' });
        setFile(null);  // Clear the file state
        return;
    }

    setFile(droppedFile);
    await analyzeFile(droppedFile);
  };

  const analyzeFile = async (pdfFile: File) => {
    try {
      // Clear any previous errors
      dispatch({ type: 'documents/setError', payload: null });
      dispatch({ type: 'documents/setLoading', payload: true });
      setUploadProgress(0);
      
      // Get authentication token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }
      
      const formData = new FormData();
      formData.append('file', pdfFile);
      
      // Log the request for debugging
      console.log(`Uploading to: ${config.API_URL}${config.endpoints.analysis}`);
      console.log(`File: ${pdfFile.name}, size: ${pdfFile.size}, type: ${pdfFile.type}`);

      // Use the correct API endpoint from config with explicit mode and credentials
      const response = await fetch(`${config.API_URL}${config.endpoints.analysis}`, {
        method: 'POST',
        body: formData,
        headers: {
          'Authorization': `Bearer ${token}`
          // Do NOT set Content-Type header for multipart/form-data
        },
        credentials: 'include',
        mode: 'cors'
      });
  
      if (!response.ok) {
        // Handle common error cases
        if (response.status === 401) {
          window.location.href = '/login';
          throw new Error('Authentication required');
        }
        
        // Try to parse error response
        let errorMsg = `Server error (${response.status})`;
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail?.message || errorData.detail || errorMsg;
        } catch (parseError) {
          // If JSON parsing fails, use text
          const errorText = await response.text();
          if (errorText) errorMsg = errorText;
        }
        
        console.error('Server error response:', errorMsg);
        throw new Error(errorMsg);
      }
  
      // Parse the response
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        console.error('Error parsing response:', parseError);
        throw new Error('Invalid response format from server');
      }
      
      if (!data) {
        throw new Error('Empty response from server');
      }
  
      dispatch({ type: 'documents/addDocument', payload: data });
      dispatch({ type: 'documents/setCurrentDocument', payload: data });
      setUploadProgress(100);
    } catch (error) {
      console.error('Error details:', error);
      dispatch({ 
        type: 'documents/setError', 
        payload: error instanceof Error ? error.message : 'Error analyzing file'
      });
      setUploadProgress(null);
    } finally {
      dispatch({ type: 'documents/setLoading', payload: false });
    }
  };

  return (
    <div className="w-full">
      <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {!currentDocument && (
          <div className="text-center mb-8 w-full">
            <h1 className="text-4xl font-bold text-white mb-4 font-display">
              Document Analysis & Insights
            </h1>
            <p className="text-purple-300 text-lg mb-8 max-w-2xl mx-auto">
              Upload your PDF documents and get instant insights with our advanced AI analysis
            </p>
          </div>
        )}

        {/* Upload Section */}
        <div className="w-full max-w-4xl mx-auto mb-8">
          <div 
            className={`
              relative group
              flex flex-col items-center justify-center
              w-full h-64 
              border-2 border-dashed
              rounded-xl cursor-pointer
              transition-all duration-300
              ${file ? 
                'border-purple-400 bg-purple-400/10' : 
                'border-purple-300/50 bg-black/20 hover:bg-black/30'
              }
              backdrop-blur-sm
            `}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => document.getElementById('file-upload')?.click()}
          >
            <div className="absolute inset-0 bg-purple-500/5 opacity-0 group-hover:opacity-100 rounded-xl transition-opacity" />
            
            <input
              id="file-upload"
              type="file"
              className="hidden"
              accept=".pdf"
              onChange={handleFileChange}
            />
            
            {file ? (
              <div className="text-center">
                <FilePlus className="w-12 h-12 text-purple-400 mx-auto mb-4" />
                <p className="text-purple-300 text-sm">
                  {file.name}
                </p>
                <p className="text-purple-400/60 text-xs mt-2">
                  Click to change file
                </p>
              </div>
            ) : (
              <>
                <div className="text-purple-400 mb-4">
                  <Upload className="w-12 h-12" />
                </div>
                <p className="mb-2 text-sm text-purple-300">
                  <span className="font-semibold">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-purple-400/60">PDF files only</p>
              </>
            )}
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg backdrop-blur-sm flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block w-12 h-12 border-4 border-purple-400/30 border-t-purple-400 rounded-full animate-spin" />
            <p className="mt-4 text-purple-300">Analyzing document...</p>
            {uploadProgress !== null && (
              <div className="mt-2 max-w-md mx-auto">
                <div className="h-2 bg-purple-900/50 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-purple-400 transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Analysis Results */}
        {currentDocument && !loading && (
          <div className="w-full bg-black/20 backdrop-blur-md rounded-xl p-6 border border-purple-500/20">
            <NERViewer analysisResults={currentDocument} />
          </div>
        )}
      </div>
    </div>
  );
};

export default MainContent;